import {
  SQDCEventActionTypeFieldType,
  SQDCEventTypeFieldRelation,
  useGetCostCentersForRainbowTileQuery,
  useGetUserForRevisionListQuery,
  useHLPCategoriesQuery,
  useQAErrorCataloguesQuery,
  useRevisionTechnicalPlacesQuery,
  type EABLoadSiteEventsQuery,
} from "@/graphql";
import { computed, unref, type MaybeRef } from "vue";
import { DateTime } from "luxon";
import { useTranslation } from "@/language/translation";
import { hasConfiguredAccessRight } from "@/functions/accessRightHelper";
import { formatDate } from "@/modules/lisaChart/helper/ChartTileHelper";
import {
  formatSQDCButtonModuleValue,
  getSQDCButtonModuleDisplayOption,
  isSQDCButtonModuleType,
  type SQDCButtonModuleOption,
} from "@/modules/sqdc/helper/SQDCButtonModuleHelper";
import { formatSQDCRepairWindowEnumValue } from "@/modules/sqdc/helper/SQDCRepairWindowHelper";
import type { ReasonType } from "./modals/SQDCIshikawaDraft";
import { normalizeIshikawaConfig, type IshikawaAreaConfig } from "./modals/ishikawaConfig";
import type { EABEventTypeType } from "./EventActionHelper";

type SiteDisplayEvent = EABLoadSiteEventsQuery["SQDCEvents"][number];
export type EABDisplayEvent = Pick<SiteDisplayEvent, "id" | "typeId"> &
  Partial<Omit<SiteDisplayEvent, "id" | "typeId">>;
export type EABDisplayField = EABEventTypeType["SQDCEventActionTypeFields"][number];
export type EABDisplayFieldRow = {
  fieldId: number;
  value?: unknown;
  userId?: number;
  percentage?: number;
  hasOutage?: boolean;
  startDate?: string;
  endDate?: string;
};
export type EABEventFieldDisplay = {
  key: number;
  label: string;
  value: string;
  badge?: Pick<SQDCButtonModuleOption, "icon" | "severity"> & { key: number; label: string };
  ishikawaDiagram?: {
    name: string;
    reasons: { name: string; type: ReasonType; selected: boolean }[];
    areas: IshikawaAreaConfig[];
  };
};

export function getEABVisibleEventFields(
  event: EABDisplayEvent,
  eventType: EABEventTypeType | null | undefined,
) {
  if (eventType == null) {
    return [];
  }
  return eventType.SQDCEventActionTypeFields.filter(
    (field) =>
      field.showOnEAB &&
      (event.revision == null ||
        (field.addedByRevision <= event.revision &&
          (field.removedByRevision == null || field.removedByRevision > event.revision))),
  )
    .toSorted(
      (left, right) =>
        left.rowIndex - right.rowIndex ||
        left.columnIndex - right.columnIndex ||
        left.id - right.id,
    )
    .map((field) => ({ field, rows: getEABEventFieldRows(event, field) }));
}

export function getEABEventFieldRows(
  event: EABDisplayEvent,
  field: EABDisplayField,
): EABDisplayFieldRow[] {
  if (field.type === SQDCEventActionTypeFieldType.ISHIKAWA) {
    return event.Ishikawa == null ? [] : [{ fieldId: field.id, value: event.Ishikawa }];
  }
  if (field.type === SQDCEventActionTypeFieldType.ENUM_NUMBER) {
    const enumValue = event.SQDCEventTextFields?.find((row) => row.fieldId === field.id)?.value;
    const numberValue = event.SQDCEventNumberFields?.find((row) => row.fieldId === field.id)?.value;
    return enumValue == null && numberValue == null
      ? []
      : [{ fieldId: field.id, value: { enumValue, numberValue } }];
  }
  if (field.type === SQDCEventActionTypeFieldType.REPAIR_WINDOW) {
    const enumValue = event.SQDCEventTextFields?.find((row) => row.fieldId === field.id)?.value;
    const dateValue = event.SQDCEventDateFields?.find((row) => row.fieldId === field.id)?.value;
    return enumValue == null && dateValue == null
      ? []
      : [{ fieldId: field.id, value: { enumValue, dateValue } }];
  }
  if (field.type === SQDCEventActionTypeFieldType.OUTAGE) {
    const rows = event.SQDCEventOutageFields?.filter((row) => row.fieldId === field.id) ?? [];
    return rows.length > 0 ? rows : [{ fieldId: field.id, hasOutage: false }];
  }

  const rows = event[eventFieldProperty(field)] as EABDisplayFieldRow[] | undefined;
  return rows?.filter((row) => row.fieldId === field.id) ?? [];
}

function eventFieldProperty(field: EABDisplayField) {
  switch (field.type) {
    case SQDCEventActionTypeFieldType.BOOLEAN:
      return "SQDCEventBooleanFields" as const;
    case SQDCEventActionTypeFieldType.DATE:
    case SQDCEventActionTypeFieldType.DATERANGE:
      return "SQDCEventDateFields" as const;
    case SQDCEventActionTypeFieldType.NUMBER:
      return "SQDCEventNumberFields" as const;
    case SQDCEventActionTypeFieldType.PERCENT_SHARING:
      return "SQDCEventPercentSharingFields" as const;
    case SQDCEventActionTypeFieldType.RELATION:
      switch (field.relation) {
        case SQDCEventTypeFieldRelation.COST_CENTER:
          return "SQDCEventCostCenterFields" as const;
        case SQDCEventTypeFieldRelation.TECHNICAL_PLACE:
          return "SQDCEventTechnicalPlaceFields" as const;
        case SQDCEventTypeFieldRelation.USER:
          return "SQDCEventUserFields" as const;
        case SQDCEventTypeFieldRelation.IDEA_CATEGORY:
          return "SQDCEventIdeaCategoryFields" as const;
        case SQDCEventTypeFieldRelation.ERROR_CATALOGUE:
          return "SQDCEventErrorCatalogueFields" as const;
      }
  }
  return "SQDCEventTextFields" as const;
}

export function parseEABFieldRecordValue(value: unknown): Record<string, unknown> | undefined {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== "string" || value.trim() === "") {
    return undefined;
  }
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed != null && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

export function useEABEventFieldLookups(events: MaybeRef<EABDisplayEvent[]>) {
  const eventList = computed(() => unref(events));
  const userIds = computed(() => [
    ...new Set(
      eventList.value.flatMap((event) => [
        ...(event.SQDCEventUserFields?.map(({ value }) => value) ?? []),
        ...(event.SQDCEventPercentSharingFields?.map(({ userId }) => userId) ?? []),
      ]),
    ),
  ]);
  const costCenterIds = computed(() => [
    ...new Set(
      eventList.value.flatMap(
        (event) => event.SQDCEventCostCenterFields?.map(({ value }) => value) ?? [],
      ),
    ),
  ]);
  const technicalPlaceIds = computed(() => [
    ...new Set(
      eventList.value.flatMap(
        (event) => event.SQDCEventTechnicalPlaceFields?.map(({ value }) => value) ?? [],
      ),
    ),
  ]);
  const errorCatalogueIds = computed(() => [
    ...new Set(
      eventList.value.flatMap(
        (event) => event.SQDCEventErrorCatalogueFields?.map(({ value }) => value) ?? [],
      ),
    ),
  ]);

  const { result: usersResult } = useGetUserForRevisionListQuery(
    () => ({ ids: userIds.value }),
    () => ({ enabled: userIds.value.length > 0, fetchPolicy: "cache-first" }),
  );
  const { result: costCentersResult } = useGetCostCentersForRainbowTileQuery(
    () => ({ ids: costCenterIds.value }),
    () => ({ enabled: costCenterIds.value.length > 0, fetchPolicy: "cache-first" }),
  );
  const { result: technicalPlacesResult } = useRevisionTechnicalPlacesQuery(
    () => ({ ids: technicalPlaceIds.value }),
    () => ({ enabled: technicalPlaceIds.value.length > 0, fetchPolicy: "cache-first" }),
  );
  const { result: ideaCategoriesResult } = useHLPCategoriesQuery();
  const { result: errorCataloguesResult } = useQAErrorCataloguesQuery(
    () => ({ ids: errorCatalogueIds.value }),
    () => ({ enabled: errorCatalogueIds.value.length > 0, fetchPolicy: "cache-first" }),
  );

  return {
    usersResult,
    costCentersResult,
    technicalPlacesResult,
    ideaCategoriesResult,
    errorCataloguesResult,
  };
}

export function useEABEventFieldDisplays(events: MaybeRef<EABDisplayEvent[]>) {
  const { t, locale } = useTranslation();
  const lookups = useEABEventFieldLookups(events);

  function getDisplays(
    event: EABDisplayEvent,
    eventType: EABEventTypeType | null | undefined,
    siteId?: number,
  ): EABEventFieldDisplay[] {
    return getEABVisibleEventFields(event, eventType).map(({ field, rows }) => {
      const hasAccess = hasConfiguredAccessRight(field.visibleForAdminOnly, siteId);
      return {
        key: field.id,
        label: t(`dynamic.${field.nameId}`),
        value: hasAccess ? formatRows(field, rows) : t("components.AccessRightWrapper.message"),
        badge: hasAccess ? getButtonBadge(field, rows) : undefined,
        ishikawaDiagram:
          hasAccess && field.type === SQDCEventActionTypeFieldType.ISHIKAWA
            ? (getIshikawaDiagram(rows[0]?.value, field.ishikawaConfig) ?? {
                name: "",
                reasons: [],
                areas: normalizeIshikawaConfig(field.ishikawaConfig, t).areas,
              })
            : undefined,
      };
    });
  }

  function getFieldValue(event: EABDisplayEvent, field: EABDisplayField) {
    return formatRows(field, getEABEventFieldRows(event, field));
  }

  function formatRows(field: EABDisplayField, rows: EABDisplayFieldRow[]) {
    return formatValue(rows.map((row) => formatFieldRow(field, row)));
  }

  function formatFieldRow(field: EABDisplayField, row: EABDisplayFieldRow): unknown {
    if (field.type === SQDCEventActionTypeFieldType.ENUM) return formatEnum(field, row.value);
    if (field.type === SQDCEventActionTypeFieldType.ENUM_NUMBER) {
      const value = parseEABFieldRecordValue(row.value);
      return value == null
        ? row.value
        : [formatEnum(field, value.enumValue), value.numberValue]
            .filter((entry) => entry != null && entry !== "")
            .join(" / ");
    }
    if (field.type === SQDCEventActionTypeFieldType.REPAIR_WINDOW) {
      const value = parseEABFieldRecordValue(row.value);
      if (value == null) return row.value;
      const enumValue =
        typeof value.enumValue === "string"
          ? formatSQDCRepairWindowEnumValue(field.EnumValues, value.enumValue, locale.value, t)
          : "";
      const dateValue =
        typeof value.dateValue === "string"
          ? formatDate(DateTime.fromISO(value.dateValue), field.dateGranularity, t)
          : "";
      return [enumValue, dateValue].filter(Boolean).join(" / ");
    }
    if (isSQDCButtonModuleType(field.type)) {
      return formatSQDCButtonModuleValue(field.type, row.value, t);
    }
    if (field.type === SQDCEventActionTypeFieldType.COMPLETION) return formatCompletion(row.value);
    if (field.type === SQDCEventActionTypeFieldType.FIVE_WHY) return formatFiveWhy(row.value);
    if (field.type === SQDCEventActionTypeFieldType.ISHIKAWA) return formatIshikawa(row.value);
    if (field.type === SQDCEventActionTypeFieldType.OUTAGE) {
      if (!row.hasOutage) {
        return t("modules.sqdc.components.eventActionBoard.modals.SQDCOutageField.noOutage");
      }
      const dates = [row.startDate, row.endDate]
        .filter((value): value is string => value != null)
        .map((value) => DateTime.fromISO(value))
        .filter((value) => value.isValid)
        .map((value) => value.toLocaleString(DateTime.DATETIME_SHORT));
      const outage = t("modules.sqdc.components.eventActionBoard.modals.SQDCOutageField.outage");
      return dates.length > 0 ? `${outage}: ${dates.join(" - ")}` : outage;
    }
    if (field.type === SQDCEventActionTypeFieldType.PERCENT_SHARING) {
      const userName =
        lookups.usersResult.value?.Users.find((user) => user.id === row.userId)?.preferredName ??
        row.userId;
      return `${userName}: ${row.percentage}%`;
    }
    if (
      [SQDCEventActionTypeFieldType.DATE, SQDCEventActionTypeFieldType.DATERANGE].includes(
        field.type,
      )
    ) {
      return typeof row.value === "string"
        ? formatDate(DateTime.fromISO(row.value), field.dateGranularity, t)
        : row.value;
    }
    if (field.type === SQDCEventActionTypeFieldType.RELATION) {
      switch (field.relation) {
        case SQDCEventTypeFieldRelation.USER:
          return (
            lookups.usersResult.value?.Users.find((user) => user.id === row.value)?.preferredName ??
            row.value
          );
        case SQDCEventTypeFieldRelation.COST_CENTER: {
          const entry = lookups.costCentersResult.value?.CostCenters.find(
            (candidate) => candidate.id === row.value,
          );
          return entry == null ? row.value : `${entry.path} ${entry.name}`;
        }
        case SQDCEventTypeFieldRelation.TECHNICAL_PLACE: {
          const entry = lookups.technicalPlacesResult.value?.OGTechnicalPlaces.find(
            (candidate) => candidate.id === row.value,
          );
          return entry == null ? row.value : `${entry.path} ${entry.name}`;
        }
        case SQDCEventTypeFieldRelation.IDEA_CATEGORY:
          return (
            lookups.ideaCategoriesResult.value?.HLPCategories.find(
              (entry) => entry.id === row.value,
            )?.name ?? row.value
          );
        case SQDCEventTypeFieldRelation.ERROR_CATALOGUE: {
          const entry = lookups.errorCataloguesResult.value?.QAErrorCatalogues.find(
            (candidate) => candidate.id === row.value,
          );
          return entry == null ? row.value : `${entry.code} – ${t(`dynamic.${entry.nameId}`)}`;
        }
      }
    }
    return row.value;
  }

  function formatEnum(field: EABDisplayField, value: unknown) {
    if (typeof value !== "string") return value;
    const entry = field.EnumValues.find((candidate) => String(candidate.languageId) === value);
    return entry == null ? value : t(`dynamic.${entry.languageId}`);
  }

  function getButtonBadge(field: EABDisplayField, rows: EABDisplayFieldRow[]) {
    if (!isSQDCButtonModuleType(field.type)) return undefined;
    const value = rows.find((row) => typeof row.value === "string")?.value;
    const option = getSQDCButtonModuleDisplayOption(field.type, value);
    return option == null
      ? undefined
      : { key: field.id, icon: option.icon, label: t(option.labelKey), severity: option.severity };
  }

  function formatValue(value: unknown): string {
    if (value == null || value === "") return "";
    if (typeof value === "boolean") return value ? t("global.yes") : t("global.no");
    if (Array.isArray(value)) return value.map(formatValue).filter(Boolean).join(", ");
    return String(value);
  }

  function formatFiveWhy(value: unknown) {
    const record = parseEABFieldRecordValue(value);
    const items = record == null ? (Array.isArray(value) ? value : []) : record.items;
    if (!Array.isArray(items)) return value;
    return items
      .map((entry) => {
        const item = parseEABFieldRecordValue(entry);
        return item == null
          ? String(entry)
          : [item.cause, item.why1, item.why2, item.why3, item.why4, item.why5]
              .filter((part): part is string => typeof part === "string" && part.trim() !== "")
              .join(" > ");
      })
      .filter(Boolean)
      .join("\n");
  }

  function formatCompletion(value: unknown) {
    const record = parseEABFieldRecordValue(value);
    if (record == null) return value;
    const checks = [
      record.presentedInSteeringCommittee ?? record.steeringCommitteePresented,
      record.reportedCompleteByTeamLead ?? record.teamLeadCompleted,
      record.closedByManager ?? record.managerClosed,
    ].filter(Boolean).length;
    const hashtags = Array.isArray(record.hashtags)
      ? record.hashtags.filter((entry): entry is string => typeof entry === "string")
      : [];
    const label = t(
      "modules.sqdc.components.eventActionBoard.modals.SQDCRevisionModal.completionChecks",
      { count: checks, total: 3 },
    );
    return hashtags.length > 0 ? `${label}: ${hashtags.join(", ")}` : label;
  }

  function formatIshikawa(value: unknown) {
    const record = parseEABFieldRecordValue(value);
    if (record == null) return value;
    const name = typeof record.name === "string" ? record.name : "";
    const reasons = Array.isArray(record.reasons)
      ? record.reasons.length
      : Array.isArray(record.Reason)
        ? record.Reason.length
        : 0;
    return reasons > 0
      ? `${name} (${t("modules.sqdc.components.eventActionBoard.modals.SQDCIshikawaField.causeCount", { count: reasons })})`
      : name;
  }

  function getIshikawaDiagram(
    value: unknown,
    configValue: unknown,
  ): EABEventFieldDisplay["ishikawaDiagram"] {
    const record = parseEABFieldRecordValue(value);
    if (record == null) return undefined;
    const reasons = Array.isArray(record.reasons)
      ? record.reasons
      : Array.isArray(record.Reason)
        ? record.Reason
        : [];
    return {
      name: typeof record.name === "string" ? record.name : "",
      areas: normalizeIshikawaConfig(configValue, t).areas,
      reasons: reasons.map((entry) => {
        const reason = parseEABFieldRecordValue(entry);
        return {
          name: typeof reason?.name === "string" ? reason.name : "",
          type:
            typeof reason?.type === "string" && reason.type.trim() !== ""
              ? (reason.type as ReasonType)
              : "HUMAN",
          selected: reason?.selected === true,
        };
      }),
    };
  }

  return { getDisplays, getFieldValue };
}
